// Z98: the single place a parsed CI is brought to its declared form -- every figure to 2 dp, CTNS 0 -> 1.
// Descartes reconciles the invoice header against the sum of the lines and errors on any difference
// (Net Weight -4178.04 incident), so the ORDER matters: round each line first, then the header is the
// sum of the ROUNDED lines (integer cents/hundredths, never float addition, never round(sum(raw))).
// Mirrors supabase/functions/submit-ecustoms/decimal.ts so UI, DB and the CDS CSV agree.
//
// INVARIANT (Gary, non-negotiable): the header PACKAGES count is the figure printed on the invoice. It is NEVER
// derived from, overwritten by, or validated against the sum of the line CTNS.
//   Why the line sum is normally larger, and that is correct: one carton can hold two different goods, half a
//   carton of each. Customs need every declared line to carry at least 1 package, so BOTH lines are written as
//   1 -- the lines add up to 2 "cartons" while the consignment is really 1 carton, and the header must stay 1.
//   The same happens with lines of 0. So 178 on the invoice vs 182 in the lines is expected, not an error:
//   do not "fix" it to agree, and do not make it a blocking check.
// Weights and amounts are the opposite on purpose: Descartes checks header == sum of lines, so those headers ARE
// the sum of the rounded lines (the invoice's own figure is kept in stated_*).
import type { InvoiceItem, InvoiceHeader, InvoiceParsed } from '../api/claudeInvoiceParser'

export const CI_DP = 2

function toUnits(v: number | string | null | undefined): number {
  const n = typeof v === 'string' ? parseFloat(v) : (v ?? 0)
  // 1e-9 nudge: 1.005 * 100 === 100.49999999999999, which should round to 1.01 like a human would
  return Number.isFinite(n) ? Math.round(Math.abs(n) * 10 ** CI_DP + 1e-9) * Math.sign(n) : 0
}

/** Round a single figure to 2 dp; null stays null ("not captured" is not zero). */
export function round2(v: number | null | undefined): number | null {
  if (v == null || !Number.isFinite(v)) return null
  return toUnits(v) / 10 ** CI_DP
}

/** Sum figures that are ALREADY rounded (or will be) using integer arithmetic. */
export function sumRounded(values: Array<number | null | undefined>): number {
  return values.reduce<number>((acc, v) => acc + toUnits(v), 0) / 10 ** CI_DP
}

/** An item whose CTNS was parsed as 0 and auto-set to 1; `ctns_original` keeps the 0 (or the raw value). */
export function isCtnsAutoAdjusted(item: Pick<InvoiceItem, 'ctns_original'>): boolean {
  return item.ctns_original != null
}

export function normaliseCiItem(item: InvoiceItem): InvoiceItem {
  const next: InvoiceItem = {
    ...item,
    gross_weight: round2(item.gross_weight),
    net_weight: round2(item.net_weight),
    amount: round2(item.amount),
  }
  // A line of 0 or a part carton (0.5, 0.23, 1.5) is declared as a whole carton, at least 1 (same rule as
  // roundCartonsForCustoms). The original is kept. null = never captured (left alone); negatives are left for
  // the save-time validation to reject.
  if (next.ctns != null && next.ctns >= 0 && next.ctns_original == null) {
    const whole = Math.max(1, Math.ceil(next.ctns))
    if (whole !== next.ctns) {
      next.ctns_original = next.ctns
      next.ctns = whole
    }
  }
  return next
}

export function normaliseCiParsed(parsed: InvoiceParsed): InvoiceParsed {
  return { ...parsed, items: parsed.items.map(normaliseCiItem) }
}

export interface CiTotals {
  total_amount: number
  gross_weight_kg: number
  net_weight_kg: number
  packages: number
}

/** Line totals as they will be declared: sums of the rounded lines. */
export function ciLineTotals(items: InvoiceItem[]): CiTotals {
  return {
    total_amount: sumRounded(items.map(i => i.amount)),
    gross_weight_kg: sumRounded(items.map(i => i.gross_weight)),
    net_weight_kg: sumRounded(items.map(i => i.net_weight)),
    packages: items.reduce((s, i) => s + (i.ctns || 0), 0),
  }
}

/**
 * Z98: bring the HEADER to its declared form: weights / amount = sum of the rounded lines (what Descartes reconciles
 * against), the invoice's own printed figure parked in stated_* the first time (never overwritten afterwards).
 * Packages are NOT touched (INVARIANT above). Call after every change to the lines so what the reviewer sees is
 * exactly what is saved and what the CDS CSV carries.
 */
export function withDeclaredTotals(parsed: InvoiceParsed): InvoiceParsed {
  const h = parsed.header
  const t = ciLineTotals(parsed.items)
  return {
    ...parsed,
    header: {
      ...h,
      stated_total_amount: h.stated_total_amount ?? h.total_amount,
      stated_gross_weight_kg: h.stated_gross_weight_kg ?? h.gross_weight_kg,
      stated_net_weight_kg: h.stated_net_weight_kg ?? h.net_weight_kg,
      total_amount: t.total_amount,
      gross_weight_kg: t.gross_weight_kg,
      net_weight_kg: t.net_weight_kg,
    },
  }
}

/** The package count that is declared in the header: the invoice's own figure, never the line sum. */
export function declaredPackages(header: Pick<InvoiceHeader, 'packages'>): number | null {
  return header.packages ?? null
}

/** Plain-information wording (not a warning): the line total is expected to exceed the invoice total. */
export function packagesNote(
  headerPackages: number | null | undefined,
  lines: Array<{ ctns: number | null; ctns_original?: number | null }>,
): string | null {
  const lineTotal = lines.reduce((s, l) => s + (l.ctns || 0), 0)
  if (headerPackages == null || headerPackages === lineTotal) return null
  const adjusted = lines.filter(l => l.ctns_original != null)
  const underOne = adjusted.filter(l => (l.ctns_original as number) < 1).length
  const partCarton = adjusted.length - underOne
  let note = `发票总箱数 ${headerPackages}（按发票申报）· 明细行合计 ${lineTotal}`
  if (underOne > 0) note += ` —— ${underOne} 行不足一箱，按规定各计 1`
  if (partCarton > 0) note += `${underOne > 0 ? '；' : ' —— '}${partCarton} 行非整箱，按规定进位`
  return note
}

export interface CiDiscrepancy {
  label: string
  header: number
  lines: number
  note: string
}

/** Header-as-stated-on-the-invoice vs the declared line totals, with the reason where we know it. */
export function ciDiscrepancies(header: InvoiceHeader, items: InvoiceItem[]): CiDiscrepancy[] {
  const t = ciLineTotals(items)
  const out: CiDiscrepancy[] = []
  const pkgNote = packagesNote(declaredPackages(header), items)
  if (pkgNote) out.push({ label: 'Packages', header: header.packages as number, lines: t.packages, note: pkgNote })
  const figs: Array<[string, number | null, number]> = [
    ['Gross Weight (kg)', header.gross_weight_kg, t.gross_weight_kg],
    ['Net Weight (kg)', header.net_weight_kg, t.net_weight_kg],
    ['Total Amount', header.total_amount, t.total_amount],
  ]
  for (const [label, h, l] of figs) {
    if (h == null) continue
    const hr = round2(h) as number
    if (hr !== l) {
      out.push({ label, header: hr, lines: l, note: `发票 ${label} ${hr.toFixed(2)} · 明细合计 ${l.toFixed(2)}（逐行取两位小数后相加），申报以明细合计为准` })
    }
  }
  return out
}
